<template>
  <div>
    <!-- Hero Section -->
    <Section>
      <div class="text-center max-w-6xl mx-auto">
        <h1 class="text-4xl md:text-6xl font-bold text-ink mb-6 leading-tight">
          {{ $t('home.hero.headline') }}
        </h1>
        <p class="text-xl text-muted mb-8 max-w-3xl mx-auto">
          {{ $t('home.hero.subheadline') }}
        </p>
        
        <div class="flex flex-col sm:flex-row gap-4 justify-center mb-12">
          <BaseButton variant="primary" size="lg" @click="$router.push('/demo')">
            {{ $t('home.hero.primaryCTA') }}
          </BaseButton>
          <BaseButton variant="secondary" size="lg" @click="$router.push('/how-it-works')">
            {{ $t('home.hero.secondaryCTA') }}
          </BaseButton>
        </div>

        <!-- Hero Image -->
        <div class="max-w-4xl mx-auto">
          <SmartScreenshot 
            image-src="/screens/unified-dashboard.png" 
            label="E2M Dashboard Overview" 
          />
        </div>
      </div>
    </Section>

    <!-- Problem/Value Section -->
    <Section background="gray">
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
        <div>
          <h2 class="text-3xl font-bold text-ink mb-6">
            {{ $t('home.problem.title') }}
          </h2>
          <p class="text-lg text-muted leading-relaxed">
            {{ $t('home.problem.description') }}
          </p>
        </div>
        <div>
          <SmartScreenshot 
            image-src="/screens/unified-control-center.png" 
            label="Unified Control Center" 
          />
        </div>
      </div>
    </Section>

    <!-- Key Features Section -->
    <Section>
      <div class="text-center mb-12">
        <h2 class="text-3xl md:text-4xl font-bold text-ink mb-4">
          {{ $t('home.keyFeatures.title') }}
        </h2>
      </div>
      
      <CardsGrid :columns="3">
        <BaseCard v-for="feature in keyFeatures" :key="feature.key" class="text-center">
          <!-- Icon -->
          <div class="w-12 h-12 bg-brand/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg class="w-6 h-6 text-brand" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" :d="getFeatureIcon(feature.key)" />
            </svg>
          </div>
          
          <h3 class="text-xl font-semibold text-ink mb-3">
            {{ $t(feature.title) }}
          </h3>
          <p class="text-muted">
            {{ $t(feature.description) }}
          </p>
        </BaseCard>
      </CardsGrid>
    </Section>

    <!-- Platform Integration Logos -->
    <Section background="gray">
      <div class="text-center mb-8">
        <p class="text-muted">Trusted by leading brands on major marketplaces</p>
      </div>
      <LogosRow />
    </Section>

    <!-- Footer CTA -->
    <Section>
      <div class="text-center max-w-3xl mx-auto">
        <h2 class="text-3xl font-bold text-ink mb-4">
          {{ $t('home.footerCTA.title') }}
        </h2>
        <p class="text-lg text-muted mb-8">
          {{ $t('home.footerCTA.description') }}
        </p>
        <BaseButton variant="primary" size="lg" @click="$router.push('/demo')">
          {{ $t('home.footerCTA.cta') }}
        </BaseButton>
      </div>
    </Section>
  </div>
</template>

<script setup lang="ts">
import { useHead } from '@vueuse/head'
import { useI18n } from 'vue-i18n'
import Section from '@/components/Section.vue'
import BaseButton from '@/components/BaseButton.vue'
import BaseCard from '@/components/BaseCard.vue'
import CardsGrid from '@/components/CardsGrid.vue'
import SmartScreenshot from '@/components/SmartScreenshot.vue'
import LogosRow from '@/components/LogosRow.vue'
import { keyFeatures } from '@/data/features'

const { t } = useI18n()

// SEO setup
useHead({
  title: () => t('home.hero.headline'),
  meta: [
    { name: 'description', content: () => t('home.hero.subheadline') },
    { property: 'og:title', content: () => t('home.hero.headline') },
    { property: 'og:description', content: () => t('home.hero.subheadline') },
    { property: 'og:type', content: 'website' }
  ]
})

// Feature icons mapping
const getFeatureIcon = (key: string) => {
  const icons = {
    'product-listings': 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
    'image-management': 'M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z',
    'performance-reports': 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z'
  }
  return icons[key as keyof typeof icons] || icons['product-listings']
}
</script>